import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface PriceCardProps {
  id: string;
  title: string;
  price: string;
  features?: string[];
  imagePath?: string;
  buttonClassName?: string;
  onBookingClick: () => void;
  showCarImage?: boolean;
  carImageSrc?: string;
}

// Helper function to format text with backticks as italics
const formatTextWithItalics = (text: string): ReactNode => {
  // Check if text contains backticks
  if (!text.includes('`')) return text;
  
  // Split the text by backticks
  const parts = text.split('`');
  const result: React.ReactNode[] = [];
  
  // Alternate between normal and italic text
  parts.forEach((part, index) => {
    if (index % 2 === 0) {
      // Even indices are regular text
      result.push(part);
    } else {
      // Odd indices should be italic
      result.push(<i key={index}>{part}</i>);
    }
  });
  
  return result;
};

const PriceCard = ({
  title,
  price,
  features,
  buttonClassName,
  onBookingClick,
  showCarImage = false,
  carImageSrc
}: PriceCardProps) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-lg p-6 shadow-md">
      {showCarImage && carImageSrc && (
        <div className="mb-4">
          <img 
            src={carImageSrc} 
            alt={`${title} car`} 
            className="w-full h-auto object-contain"
          />
        </div>
      )}
      
      <h3 className="text-xl font-bold mb-2">{formatTextWithItalics(title)}</h3>
      <p className="text-2xl font-bold mb-4">{price}</p>
      
      {features && features.length > 0 && (
        <ul className="list-disc pl-5 mb-6">
          {features.map((feature, idx) => (
            <li key={idx} className="mb-2">{formatTextWithItalics(feature)}</li>
          ))}
        </ul>
      )}
      
      <button
        className={`${buttonClassName || 'bg-blue-500'}`}
        onClick={onBookingClick}
      >
        {t('servicePage.book')}
      </button>
    </div>
  );
};

export default PriceCard; 